<template>
    <div id="rx-transfer-body">
        <b-row>
            <b-col>
                <b-row v-if="editingDisabled">
                    <b-col>
                        <b>Requested By:</b> {{ transfer.requestedBy }} <br />
                        <b>Requested On:</b> {{ convertDate(transfer.timeStamp) }}<br />
                        <b>Status:</b> {{ getRxTransferEnum(transfer.transferStatus) }}<br />
                        <a v-if="transfer.transferStatusSetBy"><b>Status Since:</b>
                            {{ convertDate(transfer.transferStatusDate) }}<br /></a>
                        <a v-if="transfer.transferStatusSetBy"><b>Status Set By:</b> {{
                            transfer.transferStatusSetBy
                        }}</a>
                    </b-col>
                </b-row>
                <b-row class="mt-2">
                    <b-col>
                        <b-form-radio :disabled="editingDisabled" v-model="transfer.inbound" :value="true" inline>
                            Inbound
                        </b-form-radio>
                        <b-form-radio :disabled="editingDisabled" v-model="transfer.inbound" :value="false" inline>
                            Outbound
                        </b-form-radio>
                    </b-col>
                </b-row>
                <b-row v-if="!editingDisabled">
                    <b-col class="mt-2">
                        <b-badge v-if="pharmacySupportsRxTransfer && !disableSurescriptsETransfer" variant="primary" class="pull-right">
                            <i class="fa fa-bolt"></i>
                            Supports Electronic RxTransfer
                        </b-badge>
                        <b-badge v-if="isTransferToSelf" variant="warning" class="pull-right mr-2">
                            <i class="fa fa-exclamation-triangle"></i>
                            Cannot transfer to your own pharmacy
                        </b-badge>
                        <b-badge v-if="pharmacySupportsRxTransfer === false && !disableSurescriptsETransfer"
                            variant="warning" class="pull-right mr-2">
                            <i class="fa fa-exclamation-triangle"></i>
                            Directory could not verify this pharmacy
                        </b-badge>
                        <b-badge v-if="isPharmacyMissingPhone" variant="warning" class="pull-right mr-2">
                            <i class="fa fa-exclamation-triangle"></i>
                            No phone number on file
                        </b-badge>
                        <search-component v-model="pharmacy" :id="pharmacy && pharmacy.id"
                            search-u-r-l="/StoredPharmacy" :params="{ showOnlyActive: true }" label="Select Pharmacy *"
                            prepopulate create-new-object createNewLabel="Create New Pharmacy"
                            @createNewObject="newStoredPharma" @input="pharmacySelected" />

                        <search-component v-model="pharmacist" :disabled="!(pharmacy && pharmacy.id)"
                            :id="pharmacist && pharmacist.id"
                            :search-u-r-l="`/api/StoredPharmacist/GetAllPharmacistsByStore/${pharmacy.id}`"
                            :params="{ showOnlyActive: true }"
                            get-by-id-u-r-l="/api/StoredPharmacist" label="Select Pharmacist" prepopulate
                            create-new-object clearable @createNewObject="newStoredPharmacist"
                            createNewLabel="Create New Pharmacist" @input="pharmacistSelected" />
                        <search-component ref="xferReason" v-model="transferReason"
                            :id="transferReason && transferReason.id" search-u-r-l="/RxTransfer/Reasons"
                            :label="transferReasonLabel" prepopulate @input="rxTransferReasonSelected" />
                        <search-component v-if="transfer.inbound" v-model="transferToStore"
                            :id="transferToStore && transferToStore.id" search-u-r-l="/Store" label="Transfer To Store *"
                            prepopulate />
                    </b-col>
                </b-row>
                <b-row v-if="editingDisabled">
                    <b-col>
                        <b-row>
                            <b-col>
                                <fg-input v-model="transfer.externalPharmacyName" label="Pharmacy" readonly />
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col cols="12">
                                <fg-input v-model="transfer.externalPharmacyPhone" label="Pharmacy Phone" readonly />
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col cols="12" class="mt-2">
                                <fg-input v-model="transfer.externalPharmacistName" label="Pharmacists" readonly />
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <fg-input v-model="transfer.rxTransferReasonName" label="Transfer Reason" readonly />
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>

                <hr>

                <b-row v-if="!editingDisabled">
                    <b-col cols="12">
                        <b-form-radio inline v-model="transfer.all" :value="false">Specific Prescriptions</b-form-radio>
                        <b-form-radio inline v-model="transfer.all" :value="true">All Prescriptions</b-form-radio>
                    </b-col>
                </b-row>

                <b-row v-if="!editingDisabled && !transfer.all">
                    <b-col cols="5">
                        <fg-input ref="drugName" v-model="newDrugName" label="Drug Name" maxlength="300" />
                    </b-col>
                    <b-col cols="3">
                        <fg-input ref="ndc" v-model="newDrugNDC" label="Drug NDC" maxlength="20" />
                    </b-col>
                    <b-col cols="2">
                        <fg-input v-model="newDrugRx" label="Rx Number" placeholder="12345678" maxlength="30" />
                    </b-col>
                    <b-col cols="2" class="mt-4">
                        <b-button size="sm" block @click="addToTransferContents" variant="success" inline>
                            Add
                        </b-button>
                    </b-col>
                </b-row>

                <hr>

                <b-table v-if="transfer.contents.length" :items="transfer.contents" :fields="fields">
                    <template #cell(goodToTransfer)="data" v-if="editingDisabled">
                        <b>{{ data.value == false ? 'Missing Pv1' : '' }}</b>
                    </template>
                    <template #cell(remove)="data" v-if="!editingDisabled">
                        <b-button size="sm" variant="danger" @click="removeDrugFromTransfer(data.item)">
                            Remove
                        </b-button>
                    </template>
                </b-table>

                <b-row>
                    <b-col>
                        <b>Notes:</b>

                        <!-- During CREATION: pending notes + emits-only form -->
                        <template v-if="!editingDisabled">
                            <div v-if="pendingNotes.length" class="px-3">
                                <NotesListItem
                                    v-for="(note, index) in pendingNotes"
                                    :key="index"
                                    :note="note"
                                    :can-reply="false"
                                    :can-pin="false"
                                />
                            </div>
                            <NotesForm
                                ref="notesForm"
                                :emits-only="true"
                                @note="handlePendingNote"
                                @input="currentNoteText = $event"
                                placeholder="Add an initial note (optional)..."
                            />
                        </template>

                        <!-- After CREATION: saved notes list + standard form -->
                        <template v-else>
                            <NotesList
                                ref="notes-list"
                                :paging="false"
                                :entity-type="EntityType.RxTransferQueue"
                                :entity-id="transfer.id"
                            />
                            <NotesForm
                                :entity-id="transfer.id"
                                :entity-type="EntityType.RxTransferQueue"
                                @saved="refreshNotesList"
                            />
                        </template>
                    </b-col>
                </b-row>

                <b-row v-if="!editingDisabled" align-h="end" align-v="baseline" class="text-right">
                    <b-col>
                        <b-checkbox v-model="sendFax" v-show="!disableFax">Send Electronic/Fax</b-checkbox>
                    </b-col>
                    <b-col cols="auto">
                        <b-button variant="success" :disabled="!isFormComplete || isSubmitting" @click="submitRxTransfer">
                            Submit
                        </b-button>
                    </b-col>
                </b-row>

                <b-row v-if="editingDisabled" align-h="end" align-v="baseline" class="text-right">
                    <b-col cols="auto">
                        <b-dropdown v-if="canChangeAnyTransferStatus" dropup text="Cancel" variant="danger">
                            <b-dropdown-item @click="changeRxTransferStatus(2, 'Not Transferable')">
                                Not Transferable
                            </b-dropdown-item>
                            <b-dropdown-item @click="changeRxTransferStatus(2, 'Insufficient Quantity')">
                                Insufficient Quantity
                            </b-dropdown-item>
                            <b-dropdown-item @click="changeRxTransferStatus(2, 'Contacting Doctor')">
                                Contacting Doctor
                            </b-dropdown-item>
                        </b-dropdown>
                        <b-dropdown v-if="canChangeAnyTransferStatus" dropup text="Change Status" variant="warning">
                            <b-dropdown-item @click="changeRxTransferStatus(0, null)">New</b-dropdown-item>
                            <b-dropdown-item @click="changeRxTransferStatus(1, null)">Open</b-dropdown-item>
                        </b-dropdown>
                        <b-button v-if="canCompleteTransfer" variant="success" :disabled="disableComplete"
                            @click="changeRxTransferStatus(3, null)">
                            Mark As Complete
                        </b-button>
                    </b-col>
                </b-row>

            </b-col>
        </b-row>
        <b-modal id="addNewPharmacy" hide-footer size="xl" no-close-on-backdrop title="Create New Pharmacy">
            <PharmacyDetailPage new-mode @close="$bvModal.hide('addNewPharmacy')" @created="onPharmacyCreate"
                :hide-title="true" :default-to-active="true" />
        </b-modal>
        <b-modal id="addNewPharmacist" hide-footer size="xl" no-close-on-backdrop title="Create New Pharmacist">
            <PharmacistDetailPage :disabled="!pharmacy || !pharmacy.id" :pharmacy-id="pharmacy && +pharmacy.id" new-mode
                @close="$bvModal.hide('addNewPharmacist')" @created="onPharmacistCreate" :hide-title="true"
                :default-to-active="true" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch, Ref } from 'vue-property-decorator';
import axios from 'axios';
import { Patient } from '@/models/Patient';
import { Prescription } from '@/models/Prescription';
import { RxTransfer, RxTransferContents, RxTransferReason, transferStatus } from '@/models/RxTransfer';
import { DatePicker } from 'element-ui';
import { Note, NoteVisibility } from '@/models/Note';
import NotesList from '@/components/Note/NotesList.vue';
import NotesForm from '@/components/Note/NotesForm.vue';
import NotesListItem from '@/components/Note/NotesListItem.vue';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import moment from 'moment';
import SearchComponent from "@/components/SearchComponent.vue";
import { PharmacyRecord } from "@/models/Pharmacy";
import { PharmacistRecord } from "@/models/Pharmacist";
import PharmacyDetailPage from "@/pages/Pharmacy/PharmacyDetailPage.vue";
import PharmacistDetailPage from "@/pages/Pharmacist/PharmacistDetailPage.vue";
import { EntityType } from '@/models/EntityType';
import { Store } from '@/models/Store';
import { getUserSetting, saveUserSetting } from "@/services/UserSettingsService";

const transferToStoreCacheKey = "rx-transfer-modal-transfer-to-store-id";

@Component({
    name: "RxTransferModalComponent",
    components: {
        PharmacistDetailPage,
        PharmacyDetailPage,
        SearchComponent,
        [DatePicker.name]: DatePicker,
        NotesList,
        NotesForm,
        NotesListItem,
    },
})
export default class RxTransferModalComponent extends Vue {
    @Prop() patient!: Patient;
    @Prop() TransferIdDetails!: number;
    public startingContents: RxTransferContents[] = [];
    protected notes: Note[] = [];
    protected pendingNotes: Note[] = [];
    protected currentNoteText: string = "";
    private transfer: RxTransfer = {
        contents: [...this.startingContents],
        notes: [...this.notes],
        inbound: undefined,
        all: false,
    } as RxTransfer;
    private newDrugName: string = "";
    private newDrugRx: string | null = null;
    private newDrugNDC: string = "";
    // The patient's Rx numbers, fetched once per modal session so bulk pre-population
    // validates against a single request instead of one GET per selected prescription.
    private patientRxNumbersCache: string[] | null = null;
    private pharmacy: PharmacyRecord = new PharmacyRecord();
    private pharmacist: PharmacistRecord = new PharmacistRecord();
    private transferReason: RxTransferReason = new RxTransferReason();
    @Ref("xferReason") xferReason?: SearchComponent;
    private transferToStore: Store = new Store();
    private pharmacySupportsRxTransfer: boolean | null = null;
    private isTransferToSelf: boolean = false;
    private disableSurescriptsETransfer: boolean = false;
    private requireTransferReasonConfig: boolean = false;
    private isSubmitting: boolean = false;

    protected sendFax: boolean = false;

    protected EntityType = EntityType;

    protected fields: any[] = [
        { key: "goodToTransfer", label: "" },
        { key: "medicationName", label: "Drug" },
        { key: "ndc", label: "NDC" },
        "rxNumber",
        { key: "remove", label: "" },
    ];

    @Watch("transferToStore")
    handleTransferToStoreChange() {
        saveUserSetting(transferToStoreCacheKey, this.transferToStore.id.toString());
    }

    @Watch('startingContents', { deep: true })
    startingContentsUpdate(_newVal: any, _oldVal: any) {
        this.populateWithStartingContents();
    }

    @Watch('patient')
    handlePatientChange() {
        // The modal instance is reused across patients (non-lazy b-modal), so drop the
        // memoized Rx-number list when the patient changes. Otherwise outbound
        // validation would run against the previous patient's prescriptions.
        this.patientRxNumbersCache = null;
    }

    @Watch("pharmacy")
    async handlePharmacyChange(newValue: PharmacyRecord, oldValue: PharmacyRecord) {
        if (!newValue.id || newValue.id === oldValue.id) return;
        const selectedId = newValue.id;
        this.pharmacySupportsRxTransfer = null;
        this.isTransferToSelf = false;
        // The saved record is enough to submit on. The directory lookup below only enriches it, so a
        // failed lookup leaves these fields in place instead of blocking the transfer.
        this.pharmacySelected();
        // Without electronic transfers there is nothing for the directory to verify, and its result
        // would be hidden anyway, so check the saved record against our own stores instead.
        if (this.disableSurescriptsETransfer) {
            await this.checkIfTransferToSelf();
            return;
        }
        try {
            const resp = await axios.get(`/RxTransfer/TransferDirectoryLookup/${selectedId}`);
            // The user may have picked another pharmacy while this lookup was in flight.
            if (this.pharmacy.id !== selectedId) return;
            this.pharmacySupportsRxTransfer = true;
            this.pharmacy = resp.data;
            this.pharmacySelected();

            await this.checkIfTransferToSelf();
        } catch (error) {
            if (this.pharmacy.id !== selectedId) return;
            console.warn('RxTransfer directory lookup failed; using the saved pharmacy record:', error);
            this.pharmacySupportsRxTransfer = false;
            this.isTransferToSelf = false;
        }
    }

    async populateWithStartingContents() {
        if (this.transfer.inbound === undefined) {
            this.transfer.inbound = false;
        }
        // This modal instance persists across opens (the host b-modal isn't lazy),
        // so start from an empty list. Otherwise reopening re-appends the current
        // selection on top of whatever was left from the previous transfer.
        this.transfer.contents = [];

        // Prime the Rx-number lookup once up front for outbound transfers. A failure
        // here surfaces a single error and aborts, instead of one failing GET plus a
        // stacked error toast for every selected prescription.
        if (!this.transfer.inbound) {
            try {
                await this.getPatientRxNumbers();
            } catch (error) {
                this.$notification(NotificationOptions.error(error));
                return;
            }
        }

        // Pass each item explicitly to addContent, which never reads the shared
        // newDrug* entry fields, so bulk population can't clobber them.
        for (const startingContent of this.startingContents) {
            await this.addContent(startingContent);
        }
    }

    async addToTransferContents() {
        // Manual "Add" button: build an item from the entry fields, then clear them
        // on success. A validation failure keeps them so the user can correct the Rx.
        const added = await this.addContent({
            medicationName: this.newDrugName,
            rxNumber: this.newDrugRx,
            ndc: this.newDrugNDC,
        });
        if (added) {
            this.newDrugRx = null;
            this.newDrugName = "";
            this.newDrugNDC = "";
        }
    }

    private async addContent(content: Pick<RxTransferContents, "medicationName" | "rxNumber" | "ndc">): Promise<boolean> {
        // Validate Rx number belongs to patient for OUTBOUND transfers only.
        // Inbound transfers use external pharmacy RX numbers which don't need validation.
        if (!this.transfer.inbound && content.rxNumber) {
            try {
                const rxNumbers = await this.getPatientRxNumbers();

                if (!rxNumbers.includes(content.rxNumber)) {
                    this.$notification(NotificationOptions.error(
                        `Rx Number ${content.rxNumber} does not belong to this patient`
                    ));
                    return false;
                }
            } catch (error) {
                this.$notification(NotificationOptions.error(error));
                return false;
            }
        }

        this.transfer.contents.push({ ...content } as RxTransferContents);
        return true;
    }

    async getPatientRxNumbers(): Promise<string[]> {
        if (this.patientRxNumbersCache === null) {
            const response = await axios.get<Prescription[]>(`/Patient/${this.patient.id}/prescriptions`);
            this.patientRxNumbersCache = response.data.map((p) => p.rxNumber?.toString());
        }
        return this.patientRxNumbersCache;
    }

    created() {
        if (this.TransferIdDetails) {
            this.getExistingTransferDetails();
        }
        this.transferToStore = Object.assign(new Store(), { id: getUserSetting(transferToStoreCacheKey) });
        this.fetchConfiguration();
    }

    async fetchConfiguration() {
        try {
            const response = await axios.get<Record<string, boolean>>(
                '/configuration/DisableSurescriptsETransfer,RequireTransferReasonOnOutboundTransfers'
            );
            this.disableSurescriptsETransfer = response.data.DisableSurescriptsETransfer ?? false;
            this.requireTransferReasonConfig = response.data.RequireTransferReasonOnOutboundTransfers ?? false;
        } catch (err) {
            console.warn('Failed to fetch eTransfer configuration:', err);
            this.disableSurescriptsETransfer = false;
            this.requireTransferReasonConfig = false;
        }
    }

    get isTransferReasonRequired(): boolean {
        // Only enforced for outbound transfers when the client has opted in.
        return this.requireTransferReasonConfig && this.transfer.inbound === false;
    }

    get transferReasonLabel(): string {
        return this.isTransferReasonRequired ? "Transfer Reason *" : "Transfer Reason";
    }

    getExistingTransferDetails() {
        axios.get(`/RxTransfer/${this.TransferIdDetails}/GetByTransferId`)
            .then(response => {
                this.transfer = response.data;
            });
    }

    removeDrugFromTransfer(drug: RxTransferContents) {
        const index = this.transfer.contents.indexOf(drug);
        if (index > -1) {
            this.transfer.contents.splice(index, 1);
        }
    }

    get isFormComplete(): boolean {
        if (!this.transfer) {
            return false;
        }
        if (!this.transfer.all && !this.transfer.contents.length) {
            return false;
        }
        if (!this.pharmacy?.name?.length) {
            return false;
        }
        if (!this.transfer.externalPharmacyName || this.transfer.externalPharmacyName == "") {
            return false;
        }
        // A saved pharmacy may have only a fax on file; either number is enough to act on the transfer.
        if (!this.transfer.externalPharmacyPhone && !this.pharmacy?.faxNumber) {
            return false;
        }
        if (this.transfer.inbound && !this.transferToStore?.id) {
            return false;
        }
        if (this.isTransferToSelf && this.transfer.inbound && this.sendFax) {
            return false;
        }
        if (this.isTransferReasonRequired && !this.transferReason?.id) {
            return false;
        }
        return this.transfer.inbound != undefined;
    }

    get isPharmacyMissingPhone(): boolean {
        return !!this.pharmacy?.id && !this.transfer.externalPharmacyPhone;
    }

    get disableComplete() {
        return this.transfer.contents.some(x => x.goodToTransfer == false);
    }

    private hasPermission(code: string): boolean {
        return (this.$permissions ?? []).some(p => p.permissionCodeName?.toLowerCase() === code.toLowerCase());
    }

    get canChangeAnyTransferStatus(): boolean {
        return this.hasPermission("Reports/Transferred Rx Report")
            || this.hasPermission("Pharmacists/Rx Transfers/Complete Transfer");
    }

    get canCompleteTransfer(): boolean {
        if (this.canChangeAnyTransferStatus) return true;
        const isOpen = this.transfer?.transferStatus === transferStatus.New
            || this.transfer?.transferStatus === transferStatus.Open;
        return this.transfer?.inbound === true && isOpen
            && !this.transfer?.requestMessageId && !this.transfer?.transferMessageId
            && this.hasPermission("Processing/Rx Intake Queue/Complete Transfer In Request");
    }

    get disableFax() {
        return !(this.transfer.inbound && this.pharmacy?.faxNumber?.length > 0);
    }

    get editingDisabled() {
        return !!this.TransferIdDetails;
    }

    pharmacySelected() {
        this.transfer.externalPharmacyId = this.pharmacy.id;
        this.transfer.externalPharmacyName = this.pharmacy.name;
        this.transfer.externalPharmacyPhone = this.pharmacy.phoneNumber;
    }

    pharmacistSelected() {
        if (isNaN(this.pharmacy.id)) return;
        this.transfer.externalPharmacistName = `${this.pharmacist?.lastName}, ${this.pharmacist?.firstName}`;
        if (!this.pharmacist) this.transfer.externalPharmacistName = "";
    }

    rxTransferReasonSelected() {
        if (isNaN(this.transferReason.id)) return;
        this.transfer.rxTransferReasonId = this.transferReason.id;
    }

    submitRxTransfer() {
        if (this.isSubmitting) return;

        // Capture any unsaved note text from the form
        if (this.currentNoteText?.trim()) {
            const isPrivate = (this.notesFormRef as any)?.private ?? false;
            this.pendingNotes.push({
                body: this.currentNoteText.trim(),
                createdBy: this.$user.userName,
                created: new Date(),
                visibility: isPrivate ? NoteVisibility.Pharmacist : NoteVisibility.AllUsers
            } as Note);
        }

        // this.transfer.inbound = this.transfer.inbound as unknown as string === 'true';
        this.transfer.rxTransferReasonId = this.transferReason.id;
        this.transfer.externalPharmacyFax = this.pharmacy.faxNumber;
        this.transfer.patientId = Number(this.patient.id);
        this.transfer.transferToStoreId = this.transferToStore.id;
        this.transfer.notes = this.pendingNotes;

        this.isSubmitting = true;
        axios.post(`/api/RxTransfer/${this.patient.id}/AddToRxTransferQueue`,
            this.transfer,
            { params: { sendFax: this.transfer.inbound && this.sendFax } })
            .then(_response => {
                this.$notification(NotificationOptions.notificationOptionsPreset("Transfer Created Successfully", NotificationOptions.NotificationTypes.success));
                this.$emit("complete");
            })
            .catch((err) => {
                this.$notification(NotificationOptions.notificationOptionsPreset("Failed To Create Transfer", NotificationOptions.NotificationTypes.danger));
                this.$notification(NotificationOptions.error(err));
            })
            .finally(() => {
                this.isSubmitting = false;
            });
    }

    changeRxTransferStatus(statusId: number, reason: string | null) {
        if (!reason) {
            reason = null;
        }
        axios.post(`/RxTransfer/${this.TransferIdDetails}/${this.transfer.patientId}/ChangeStatus/${statusId}/${reason}`)
            .then(_response => {
                this.$notification(NotificationOptions.notificationOptionsPreset("Change Saved", NotificationOptions.NotificationTypes.success));
                this.$emit("complete");
            })
            .catch(error => {
                this.$notification(NotificationOptions.error(error));
            });
    }

    convertDate(date: string | Date) {
        return moment.utc(date).format("L");
    }

    getRxTransferEnum(status: number) {
        return transferStatus[status];
    }

    newStoredPharma() {
        this.pharmacy = new PharmacyRecord();
        this.$bvModal.show("addNewPharmacy");
    }

    newStoredPharmacist() {
        this.pharmacist = new PharmacistRecord();
        this.$bvModal.show("addNewPharmacist");
    }

    onPharmacyCreate(newPharma: PharmacyRecord) {
        this.pharmacy = newPharma;
        this.$bvModal.hide("addNewPharmacy");
    }

    onPharmacistCreate(newPharmacist: PharmacistRecord) {
        this.pharmacist = newPharmacist;
        this.$bvModal.hide("addNewPharmacist");
        this.$nextTick(() => {
            setTimeout(() => {
                this.xferReason?.focus();
            }, 300);
        });
    }

    @Ref("notes-list") protected notesList!: NotesList;
    @Ref("notesForm") protected notesFormRef?: NotesForm;

    refreshNotesList() {
        this.notesList.loadNotes();
    }

    handlePendingNote(note: Note) {
        this.pendingNotes.push(note);
    }

    async checkIfTransferToSelf() {
        try {
            if (!this.pharmacy?.ncpdp && !this.pharmacy?.npiNumber) {
                this.isTransferToSelf = false;
                return;
            }

            const storesResp = await axios.get('/Store');
            const stores = storesResp.data;

            if (!stores || !Array.isArray(stores)) {
                this.isTransferToSelf = false;
                return;
            }

            // Check if selected pharmacy matches any of our stores by NCPDP or NPI
            this.isTransferToSelf = stores.some((store: any) =>
                (this.pharmacy.ncpdp && store.ncpdp &&
                 this.pharmacy.ncpdp.trim().toLowerCase() === store.ncpdp.trim().toLowerCase()) ||
                (this.pharmacy.npiNumber && store.npiNumber &&
                 this.pharmacy.npiNumber.trim().toLowerCase() === store.npiNumber.trim().toLowerCase())
            );
        } catch (error) {
            console.error('Error checking if transfer to self:', error);
            this.isTransferToSelf = false;
        }
    }
}
</script>

<style scoped></style>
